import apiClient from "@/api/api";
import { updateBaFields } from "@/api/update-survey-api";
import { useProperties } from "@/composables/Property/useProperties";
import { useStore } from "@/composables/useTypedStore";
import useUser from "@/composables/useUser";
import Bestandsaufnahme from '@/models/ba/Bestandsaufnahme';
import { HzbaStatusCode, HzbaStatusKey, translatedHzbaStatus } from "@/models/ba/interfaces/IBestandsaufnahme";
import BestandsaufnahmeModel from '@/models/ba/models/bestandsaufnahme.model';
import Immobilie from '@/models/immobilie.model';
import { Monitoring } from "@/utilities/monitoring";
import { alertController, AlertInput } from "@ionic/vue";
import { default as qs } from 'qs';
import { computed } from "vue";

export interface FilterOption {
    name: string,
    id: string,
    resetOtherFilters?: boolean,
    resetSelfWhenOthersActive?: boolean,
    onlyShowWhenOthersActive?: boolean;
    dontShowWhenNotSelected?: boolean;
}

export function useBestandsaufnahmens() {

    const store = useStore();
    const { user } = useUser();

    const featureFlags = computed(() => user.value!.organisation?.featureFlags)

    const currentProject = computed(() => store.state.user.currentUserProject)

    const setBestandsaufnahmeStatus = (ba: Bestandsaufnahme) => async (status: HzbaStatusKey) => {
        try {
            const isOnline = store.getters["app/isOnline"];
            let updatedBaJson = null;

            if (isOnline) {
                const oldSurvey = await ba.toClassJson();
                updatedBaJson = await updateBaFields(ba.id, currentProject.value.id, { status }, oldSurvey);
            } else {
                updatedBaJson = await ba.toClassJson();
                updatedBaJson.status = status;
                updatedBaJson.hasUnsavedChanges = true;
            }
            await BestandsaufnahmeModel.dispatch('storeMoverSurvey', updatedBaJson);

            if(updatedBaJson && ba.id === store.state.currentHzba?.currentBa?.id) {
                updatedBaJson.isLocal = false; // legacy property, make sure it is not set to true
                const updatedBa = new Bestandsaufnahme(updatedBaJson);
                store.commit('currentHzba/setCurrentBa', updatedBa);
            }

        } catch (error: any) {
            Monitoring.chainError("Error while setting status of Bestandsaufnahme", error);
        }
    }

    const openChangeStatusAlert = async (ba: Bestandsaufnahme, t: any) => {
        if(!featureFlags?.value.survey?.changeStatus) {
            return;
        }
        const alertButtons = [
          {
            text: t('hzba.buttons.confirm'),
            handler: setBestandsaufnahmeStatus(ba)
          },
          t('hzba.buttons.cancel')
        ];
        const statuses: AlertInput[] = [
            HzbaStatusCode.ANGELEGT,
            HzbaStatusCode.GEPLANT,
            HzbaStatusCode.IN_DURCHFUEHRUNG,
            HzbaStatusCode.ABGESCHLOSSEN,
            HzbaStatusCode.FREIGEGEBEN,
            HzbaStatusCode.ARCHIVIERT
        ].map((code) => ({
            label: translatedHzbaStatus(code, t),
            type: "radio",
            value: code,
            checked: ba.status === code
        }));

        const alert = await alertController.create({
          header: t("hzba.updateStatus"),
          buttons: alertButtons,
          inputs: statuses
        });
        alert.present();
    }

    /**
     * Remove downloaded Ba from indexedDb
     * @param ba
     */
    const removeDownloadedBa = async (ba?: Bestandsaufnahme) => {
        const immobilie = ba?.immobilie && Immobilie.find(ba?.immobilie) as (Immobilie | undefined);
        if (!ba) {
            Monitoring.error('removeDownloadedBa: ba is undefined')
            return;
        }

        const baModel = BestandsaufnahmeModel.find(ba.id);
        if (baModel && immobilie) {
            // baModel.isDownloaded = false;
            ba.isDownloaded = false;

            await BestandsaufnahmeModel.dispatch('$deleteFromLocal', baModel.id);

            // with current API it's not possible to  delete only from local storage but not from store, thus we need to insert afterwards again
            // Monitoring.consoleOnly("remove downloads add to vuex", await ba.toClassJson());

            // await BestandsaufnahmeModel.dispatch('removeFromPersistedBestandsaufnahmes', baModel);
            await BestandsaufnahmeModel.insertOrUpdate({ data: await ba.toClassJson() });

            const { considerRemoveImmobilieFromDownloaded } = useProperties();
            await considerRemoveImmobilieFromDownloaded(baModel.immobilie)
        }
    }

    const doLocalBestandsaufnahmesExists = async () => {
        return BestandsaufnahmeModel.all().find(el => el.isLocal);
    }

    const isUpgradeAvailable = async (ba: Bestandsaufnahme) => {
        const res = await apiClient.get(`/bestandsaufnahmes/isUpgradeAvailable/${ba.id}`);
        return res.data;
    }

    const migrateBa = async (ba: Bestandsaufnahme, preview: Boolean) => {
        const res = await apiClient.put(`/bestandsaufnahmes/upgradeTemplate/${ba.id}`, { preview: preview });
        return res.data
    }


    const getBackupsForBa = async (baId: number) => {
        const query = qs.stringify({
            sort: ['createdAt:desc'],
            filters: {
                bestandsaufnahme: {
                    id: {
                        $eq: baId,
                    },
                },
            },
            fields: ['createdAt'],
            pagination: {
                pageSize: 10,
                page: 1,
            },
            publicationState: 'live',
            locale: ['en'],
        }, { encodeValuesOnly: true });

        const res = await apiClient.get(`/backup-bestandsaufnahmes?${query}`)
        return res.data.data;
    }

    const runBackup = async (backupId: number) => {
        const res = await apiClient.put(`/bestandsaufnahmes/runBackup/${backupId}`)
        return res;
    }

    return {
        removeDownloadedBa,
        runBackup,
        getBackupsForBa,
        isUpgradeAvailable,
        doLocalBestandsaufnahmesExists,
        migrateBa,
        openChangeStatusAlert
    };
}
